import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribe,
  getDashboardOverview,
  getFrameworkCompliance,
  getRiskAssessment,
  getKPIs,
  getCriticalAlerts,
  getUpcomingDeadlines,
  getPerformanceTrends,
  getBenchmarking,
  refreshData
} from '../../services/ethics/ethicsDashboardService';
import { add as addAuditLog } from '../../services/bread/auditLogService';

const Ethics = () => {
  const [ethicsData, setEthicsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedFramework, setSelectedFramework] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribe(setEthicsData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'ethics_dashboard', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = () => {
    setLoading(true);
    refreshData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'ethics_dashboard', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const navigateToSection = (section) => {
    const routeMap = {
      'supply-chain': '/ethics/supply-chain-ethics',
      'cultural-global': '/ethics/cultural-global-impact',
      'supplier-ecosystem': '/ethics/supplier-ecosystem'
    };
    
    if (routeMap[section]) {
      navigate(routeMap[section]);
    }
  };

  if (loading || !ethicsData) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading ethics dashboard...</span>
          </div>
          <p className="mt-2">Loading Ethics Department Dashboard...</p>
        </div>
      </div>
    );
  }

  const overview = getDashboardOverview();
  const frameworks = getFrameworkCompliance();
  const riskAssessment = getRiskAssessment();
  const kpis = getKPIs();
  const criticalAlerts = getCriticalAlerts();
  const upcomingDeadlines = getUpcomingDeadlines();
  const performanceTrends = getPerformanceTrends();
  const benchmarking = getBenchmarking();

  const OverviewTab = () => (
    <div className="row">
      {/* Executive Summary Cards */}
      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-primary text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.overallEthicsScore}%</div>
            <div className="mb-2">Overall Ethics Score</div>
            <small>
              <i className={`fas fa-arrow-${overview.trend === 'Improving' ? 'up text-success' : 'down text-warning'} me-1`}></i>
              {overview.trend}
            </small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-success text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.trainingCompletionRate}%</div>
            <div className="mb-2">Training Completion</div>
            <small>{overview.trainedEmployees.toLocaleString()} of {overview.totalEmployees.toLocaleString()} employees</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-warning text-dark">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.activeIncidents}</div>
            <div className="mb-2">Active Incidents</div>
            <small>{overview.incidentResolutionRate}% resolution rate</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-info text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{overview.policyAcknowledgmentRate}%</div>
            <div className="mb-2">Policy Compliance</div>
            <small>Current acknowledgment rate</small>
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      {criticalAlerts.length > 0 && (
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>Critical Ethics Alerts
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                {criticalAlerts.map((alert, index) => (
                  <div key={index} className="col-md-4 mb-3">
                    <div className="card border-danger">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{alert.type}</h6>
                          <span className="badge bg-danger">{alert.severity}</span>
                        </div>
                        <p className="small mb-2">{alert.message}</p>
                        <div className="mb-2">
                          <small><strong>Action:</strong> {alert.action}</small>
                        </div>
                        <div className="d-flex justify-content-between">
                          <small><strong>Assigned:</strong> {alert.assignedTo}</small>
                          <small><strong>Due:</strong> {new Date(alert.dueDate).toLocaleDateString()}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance Trends */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>Performance Trends
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Ethics Score</span>
                <span className="text-success">
                  <i className="fas fa-arrow-up me-1"></i>{performanceTrends.ethicsScore.trend}
                </span>
              </div>
              <div className="progress mb-1" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  style={{ width: `${performanceTrends.ethicsScore.current}%` }}
                ></div>
              </div>
              <small className="text-muted">{performanceTrends.ethicsScore.current}% (Target: {performanceTrends.ethicsScore.target}%)</small>
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Training Completion</span>
                <span className="text-success">
                  <i className="fas fa-arrow-up me-1"></i>{performanceTrends.trainingCompletion.trend}
                </span>
              </div>
              <div className="progress mb-1" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${performanceTrends.trainingCompletion.current}%` }}
                ></div>
              </div>
              <small className="text-muted">{performanceTrends.trainingCompletion.current}% (Target: {performanceTrends.trainingCompletion.target}%)</small>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Policy Compliance</span>
                <span className="text-success">
                  <i className="fas fa-arrow-up me-1"></i>{performanceTrends.policyCompliance.trend}
                </span>
              </div>
              <div className="progress mb-1" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-info" 
                  style={{ width: `${performanceTrends.policyCompliance.current}%` }}
                ></div>
              </div>
              <small className="text-muted">{performanceTrends.policyCompliance.current}% (Target: {performanceTrends.policyCompliance.target}%)</small>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-tachometer-alt me-2"></i>Key Performance Indicators
            </h5>
          </div>
          <div className="card-body">
            {Object.entries(kpis).map(([key, kpi]) => (
              <div key={key} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-0">{kpi.description}</h6>
                    <small className="text-muted">Current: {kpi.current} | Target: {kpi.target}</small>
                  </div>
                  <div className="text-center">
                    <div className={`h5 mb-0 ${
                      kpi.trend === 'Improving' ? 'text-success' : 
                      kpi.trend === 'Declining' ? 'text-danger' : 'text-warning'
                    }`}>
                      {kpi.current}
                    </div>
                    <small className={`text-${
                      kpi.trend === 'Improving' ? 'success' : 
                      kpi.trend === 'Declining' ? 'danger' : 'warning'
                    }`}>
                      <i className={`fas fa-arrow-${
                        kpi.trend === 'Improving' ? 'up' : 
                        kpi.trend === 'Declining' ? 'down' : 'right'
                      } me-1`}></i>
                      {kpi.trend}
                    </small>
                  </div>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar bg-${
                      kpi.current >= kpi.target ? 'success' : 
                      kpi.current >= kpi.target * 0.8 ? 'warning' : 'danger'
                    }`}
                    style={{ width: `${Math.min(100, (kpi.current / kpi.target) * 100)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Department Quick Access */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-sitemap me-2"></i>Ethics Department Modules
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="card border-primary h-100">
                  <div className="card-body text-center">
                    <i className="fas fa-truck fa-3x text-primary mb-3"></i>
                    <h6 className="card-title">Supply Chain Ethics</h6>
                    <p className="card-text small">ESG compliance, supplier assessment, and ethical sourcing management</p>
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => navigateToSection('supply-chain')}
                    >
                      <i className="fas fa-arrow-right me-1"></i>Access Module
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="card border-success h-100">
                  <div className="card-body text-center">
                    <i className="fas fa-globe fa-3x text-success mb-3"></i>
                    <h6 className="card-title">Cultural & Global Impact</h6>
                    <p className="card-text small">Cultural sensitivity, global compliance, and international ethics standards</p>
                    <button 
                      className="btn btn-outline-success btn-sm"
                      onClick={() => navigateToSection('cultural-global')}
                    >
                      <i className="fas fa-arrow-right me-1"></i>Access Module
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="card border-info h-100">
                  <div className="card-body text-center">
                    <i className="fas fa-users fa-3x text-info mb-3"></i>
                    <h6 className="card-title">Supplier Ecosystem</h6>
                    <p className="card-text small">Supplier lifecycle, onboarding, performance monitoring, and ecosystem management</p>
                    <button 
                      className="btn btn-outline-info btn-sm"
                      onClick={() => navigateToSection('supplier-ecosystem')}
                    >
                      <i className="fas fa-arrow-right me-1"></i>Access Module
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Industry Benchmarking */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-trophy me-2"></i>Industry Benchmarking
            </h5>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h4 text-success">{benchmarking.ranking.overall}</div>
              <p className="text-muted">Overall Industry Ranking</p>
            </div>
            
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <small><strong>Ethics Score</strong></small>
                <small>Us: {benchmarking.competitorComparison.ethicsScore.us}% | Industry: {benchmarking.competitorComparison.ethicsScore.industry}%</small>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div className="progress-bar bg-primary" style={{ width: `${benchmarking.competitorComparison.ethicsScore.us}%` }}></div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <small><strong>Training Completion</strong></small>
                <small>Us: {benchmarking.competitorComparison.trainingCompletion.us}% | Industry: {benchmarking.competitorComparison.trainingCompletion.industry}%</small>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div className="progress-bar bg-success" style={{ width: `${benchmarking.competitorComparison.trainingCompletion.us}%` }}></div>
              </div>
            </div>

            <div className="text-center">
              <span className="badge bg-success">{benchmarking.ranking.improvement}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Deadlines */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-calendar-alt me-2"></i>Upcoming Deadlines
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <h6 className="text-primary">Training Deadlines</h6>
              {upcomingDeadlines.training.slice(0, 3).map((deadline, index) => (
                <div key={index} className="mb-2 p-2 border rounded">
                  <div className="d-flex justify-content-between">
                    <small><strong>{deadline.program}</strong></small>
                    <span className={`badge bg-${deadline.risk === 'High' ? 'danger' : 'warning'}`}>
                      {deadline.risk}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small>{deadline.pendingUsers} pending users</small>
                    <small>Due: {new Date(deadline.dueDate).toLocaleDateString()}</small>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-3">
              <h6 className="text-info">Policy Reviews</h6>
              {upcomingDeadlines.policies.slice(0, 2).map((review, index) => (
                <div key={index} className="mb-2 p-2 border rounded">
                  <div className="d-flex justify-content-between">
                    <small><strong>{review.policy}</strong></small>
                    <span className={`badge bg-${review.priority === 'High' ? 'danger' : 'info'}`}>
                      {review.priority}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small>Owner: {review.owner}</small>
                    <small>Due: {new Date(review.dueDate).toLocaleDateString()}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ComplianceTab = () => (
    <div className="row">
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-shield-alt me-2"></i>Industry Standard Framework Compliance
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {Object.entries(frameworks).map(([key, framework]) => (
                <div key={key} className="col-md-6 mb-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">{framework.name}</h6>
                      <span className={`badge bg-${
                        framework.status === 'Compliant' ? 'success' : 
                        framework.status === 'Action Required' ? 'warning' : 'danger'
                      }`}>
                        {framework.status}
                      </span>
                    </div>
                    <div className="card-body">
                      <p className="small text-muted mb-3">{framework.description}</p>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small><strong>Compliance Score</strong></small>
                          <small>{framework.complianceScore}%</small>
                        </div>
                        <div className="progress mb-2" style={{ height: '8px' }}>
                          <div 
                            className={`progress-bar bg-${
                              framework.complianceScore >= 90 ? 'success' : 
                              framework.complianceScore >= 75 ? 'warning' : 'danger'
                            }`}
                            style={{ width: `${framework.complianceScore}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="fw-bold text-muted">Key Sections:</small>
                        {framework.sections.map((section, idx) => (
                          <div key={idx} className="small text-muted">• {section}</div>
                        ))}
                      </div>

                      <div className="d-flex justify-content-between">
                        <small><strong>Last Assessment:</strong> {new Date(framework.lastAssessment).toLocaleDateString()}</small>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSelectedFramework(framework)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const RiskTab = () => (
    <div className="row">
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>Ethics Risk Assessment
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="text-warning">{riskAssessment.overallRiskScore}/10</h4>
                  <p className="text-muted">Overall Risk Level: <strong>{riskAssessment.riskLevel}</strong></p>
                </div>
                <div className="text-end">
                  <small>Last Assessment: {new Date(riskAssessment.lastAssessment).toLocaleDateString()}</small><br/>
                  <small>Next Assessment: {new Date(riskAssessment.nextAssessment).toLocaleDateString()}</small>
                </div>
              </div>
            </div>

            <div className="row">
              {Object.entries(riskAssessment.categories).map(([key, category]) => (
                <div key={key} className="col-md-6 mb-3">
                  <div className={`card border-${
                    category.level === 'High' ? 'danger' : 
                    category.level === 'Medium' ? 'warning' : 'success'
                  }`}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">{category.name}</h6>
                        <span className={`badge bg-${
                          category.level === 'High' ? 'danger' : 
                          category.level === 'Medium' ? 'warning' : 'success'
                        }`}>
                          {category.level}
                        </span>
                      </div>
                      
                      <div className="mb-2">
                        <small><strong>Risk Score:</strong> {category.score}/10</small>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className={`progress-bar bg-${
                              category.score >= 7 ? 'danger' : 
                              category.score >= 4 ? 'warning' : 'success'
                            }`}
                            style={{ width: `${(category.score / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <small><strong>Controls:</strong> {category.controls} active</small><br/>
                        <small><strong>Incidents:</strong> {category.incidents} this quarter</small><br/>
                        <small><strong>Trend:</strong> 
                          <span className={`text-${
                            category.trend === 'Improving' ? 'success' : 
                            category.trend === 'Declining' ? 'danger' : 'warning'
                          }`}>
                            <i className={`fas fa-arrow-${
                              category.trend === 'Improving' ? 'down' : 
                              category.trend === 'Declining' ? 'up' : 'right'
                            } ms-1`}></i> {category.trend}
                          </span>
                        </small>
                      </div>

                      <div>
                        <small className="fw-bold text-muted">Mitigation:</small>
                        {category.mitigation.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="small text-muted">• {item}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-tasks me-2"></i>Risk Mitigation Status
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">Implemented Controls</span>
                <span className="badge bg-success">{riskAssessment.mitigation.implemented}</span>
              </div>
              <div className="progress mb-3" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${(riskAssessment.mitigation.implemented / (riskAssessment.mitigation.implemented + riskAssessment.mitigation.planned + riskAssessment.mitigation.inProgress)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">In Progress</span>
                <span className="badge bg-warning text-dark">{riskAssessment.mitigation.inProgress}</span>
              </div>
              <div className="progress mb-3" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-warning" 
                  style={{ width: `${(riskAssessment.mitigation.inProgress / (riskAssessment.mitigation.implemented + riskAssessment.mitigation.planned + riskAssessment.mitigation.inProgress)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">Planned</span>
                <span className="badge bg-secondary">{riskAssessment.mitigation.planned}</span>
              </div>
              <div className="progress mb-3" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-secondary" 
                  style={{ width: `${(riskAssessment.mitigation.planned / (riskAssessment.mitigation.implemented + riskAssessment.mitigation.planned + riskAssessment.mitigation.inProgress)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="text-center">
              <button className="btn btn-outline-info btn-sm">
                <i className="fas fa-download me-1"></i>Export Risk Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Ethics Department</h2>
              <p className="text-muted">Industry-standard ethics management and compliance dashboard</p>
            </div>
            <button 
              className="btn btn-outline-primary"
              onClick={handleRefreshData}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills">
            {[
              { id: 'overview', label: 'Dashboard Overview', icon: 'fas fa-tachometer-alt' },
              { id: 'compliance', label: 'Framework Compliance', icon: 'fas fa-shield-alt' },
              { id: 'risk', label: 'Risk Assessment', icon: 'fas fa-exclamation-triangle' }
            ].map(tab => (
              <li key={tab.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>{tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'compliance' && <ComplianceTab />}
        {activeTab === 'risk' && <RiskTab />}
      </div>

      {/* Framework Detail Modal */}
      {selectedFramework && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-shield-alt me-2"></i>
                  {selectedFramework.name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedFramework(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Framework Overview</h6>
                    <p className="text-muted">{selectedFramework.description}</p>
                    <div className="mb-3">
                      <strong>Compliance Score:</strong> {selectedFramework.complianceScore}%
                    </div>
                    <div className="mb-3">
                      <strong>Status:</strong> 
                      <span className={`badge bg-${
                        selectedFramework.status === 'Compliant' ? 'success' : 
                        selectedFramework.status === 'Action Required' ? 'warning' : 'danger'
                      } ms-2`}>
                        {selectedFramework.status}
                      </span>
                    </div>
                    <div className="mb-3">
                      <strong>Last Assessment:</strong> {new Date(selectedFramework.lastAssessment).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Key Sections</h6>
                    {selectedFramework.sections.map((section, index) => (
                      <div key={index} className="mb-2 p-2 border rounded">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">{section}</span>
                          <span className="badge bg-success">Compliant</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedFramework(null)}
                >
                  Close
                </button>
                <button type="button" className="btn btn-primary">
                  Generate Compliance Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ethics;